import { createContext, useContext, useEffect, useRef, useState } from 'react';

/**
 * A business review's snapshot — what its deck showed, kept on the review
 * (migration 150) so that opening it next month shows what was presented on
 * the day, not this month's numbers.
 *
 * The deck provides the snapshot through SnapshotContext. A slide that reads
 * live data asks for its part with useCaptured: the saved payload when there
 * is one; otherwise it loads the live data ONCE and saves it. Refresh loads
 * again and replaces the saved part.
 */

export type SnapshotPart = 'recommendations' | 'audits' | 'security' | 'tickets6m' | 'tickets3m' | 'csat12m' | 'services' | 'projects' | 'assets' | 'warranties' | 'agentHealth';

export interface Snap {
  payload: unknown;
  capturedUtc: string;
  capturedBy: string | null;
}

interface SnapshotApi {
  get: (part: SnapshotPart) => Snap | undefined;
  save: (part: SnapshotPart, payload: unknown) => Promise<void>;
  /** Viewers can see a snapshot but not take or replace one. */
  canWrite: boolean;
  /** Bumped when the whole snapshot is thrown away, so every part captures again. */
  generation: number;
}

export const SnapshotContext = createContext<SnapshotApi | null>(null);

export const capturedLabel = (utc: string | null, by: string | null) =>
  utc
    ? `Captured ${new Date(utc).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}${by ? ` by ${by.split('@')[0]}` : ''}`
    : null;

/**
 * One part of the snapshot. `load(refresh)` reads the live data; `keep`
 * decides whether a result is worth saving — "not found in Autotask" is not,
 * so it is tried again next time rather than frozen.
 */
export function useCaptured<T>(part: SnapshotPart, load: (refresh: boolean) => Promise<T>, keep: (t: T) => boolean = () => true) {
  const ctx = useContext(SnapshotContext);
  if (!ctx) throw new Error('useCaptured needs a SnapshotContext');
  const saved = ctx.get(part);
  const [live, setLive] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tried = useRef<number | null>(null);

  useEffect(() => {
    if (saved || tried.current === ctx.generation) return;
    tried.current = ctx.generation;
    setBusy(true);
    setError(null);
    setLive(null);
    load(false)
      .then(async (d) => {
        setLive(d);
        if (ctx.canWrite && keep(d)) await ctx.save(part, d).catch(() => undefined);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not read the data'))
      .finally(() => setBusy(false));
    // load and keep are the slide's own; the part and generation decide when to read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, part, ctx.generation, ctx.canWrite]);

  const refresh = async () => {
    if (!window.confirm('Replace the data captured for this slide with what is there now? The figures from the day of the review will be gone.')) return;
    setBusy(true);
    setError(null);
    try {
      const d = await load(true);
      setLive(d);
      if (keep(d)) await ctx.save(part, d);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the data');
    } finally {
      setBusy(false);
    }
  };

  const data = (saved?.payload as T | undefined) ?? live;
  return {
    data,
    loading: data === null && !error,
    error,
    refreshing: busy && data !== null,
    capturedUtc: saved?.capturedUtc ?? null,
    capturedBy: saved?.capturedBy ?? null,
    canWrite: ctx.canWrite,
    refresh,
  };
}

/** The line under a captured slide: when it was taken, and Refresh. */
export function CapturedNote({
  capturedUtc,
  capturedBy,
  canWrite,
  refreshing,
  onRefresh,
}: {
  capturedUtc: string | null;
  capturedBy: string | null;
  canWrite: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="am-captured no-print">
      {capturedLabel(capturedUtc, capturedBy) ?? 'Live — not saved yet'}
      {canWrite && (
        <>
          {' · '}
          <button type="button" className="link-button" disabled={refreshing} onClick={onRefresh}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </>
      )}
    </div>
  );
}
